import React, { useState, useCallback, useEffect, useRef } from "react";
import { isFunction } from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Button,
  VStack,
  Checkbox,
  FormControl,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as R from "ramda";
import * as Yup from "yup";

import { useApi } from "utils/useApi";
import {
  FLOW_STATE,
  isBooleanTrue,
  isTrue,
  useSessionCtx,
  useSessionJWT,
  noop,
  useAnalytics,
} from "utils";
import { ALLOWBYPASSTOS, REQUESTBYPASSTOS } from "utils/constants";
import { getTOSStatus, signTiliaTOS } from "api/accounts";
import EmptyState from "components/EmptyState";
import { PrivacyPolicyModalLinkButton, TOSModalLinkButton } from "./Modal";
import TiliaAPIError from "api/TiliaAPIError";
import { useFlowConfig } from "flows/hooks";
import Header from "flows/components/Header";

const MIN_HEIGHT = "320px"; // in the case of widget script being used with 'resize: true', it's possible the height for the TOS/Privacy modal to only be the height of the TOS flow UI.  With this, we make sure there's enough height for modal to not look weird/small

const CheckboxFormControl = ({ errorMessage, ...rest }) => {
  return (
    <FormControl isInvalid={rest.meta.touched && rest.meta.error}>
      <Checkbox {...rest} spacing="1rem" isInvalid={rest.meta.touched && rest.meta.error} />
      <FormErrorMessage pl={8}>
        {rest.meta.error && isFunction(errorMessage) && errorMessage()}
      </FormErrorMessage>
    </FormControl>
  );
};

const FormContents = ({ isSubmitting, values }) => {
  const { formatMessage } = useIntl();
  const localizedTosErrosMessage = useCallback(
    () =>
      formatMessage({
        defaultMessage: "You must agree to the Terms of Service and Privacy Policy to continue",
        description: "You must aerror message",
      }),
    [formatMessage]
  );
  const submitDisabled = isSubmitting || values.tosAndPpCheckbox !== true; // TODO: fix formik isValid not working as expected.  Sometimes tosAndPpCheckbox is true (which is all isValid needs) and yet there is stil an error for it
  return (
    <VStack data-testid="tos-form" as={Form} spacing={6} align="stretch">
      <VStack spacing={2} align="stretch">
        <Field name="tosAndPpCheckbox">
          {({ field, meta }) => (
            <CheckboxFormControl
              errorMessage={localizedTosErrosMessage}
              {...field}
              meta={meta}
              data-testid="tos-and-pp-checkbox"
            >
              <FormattedMessage
                defaultMessage="I agree to Tilia LLC's <tos>Terms of Service</tos> and acknowledge receipt of Tilia LLC's <privacy>Privacy Policy</privacy>"
                values={{
                  tos: (msg) => <TOSModalLinkButton label={msg} size="md" />,
                  privacy: (msg) => <PrivacyPolicyModalLinkButton label={msg} size="md" />,
                }}
                description="message with links"
              />
            </CheckboxFormControl>
          )}
        </Field>
      </VStack>

      <Button
        data-testid="submitButton"
        isDisabled={submitDisabled}
        isLoading={isSubmitting}
        loadingText={formatMessage({
          defaultMessage: "Hang tight...",
          description: "button label",
        })}
        type="submit"
        width="full"
      >
        <FormattedMessage defaultMessage="Accept" description="button label" />
      </Button>
    </VStack>
  );
};

const InnerTOSForm = ({ onReady = noop, onComplete, onError }) => {
  const { hideExitButtons } = useFlowConfig();
  const { sessionJWT } = useSessionJWT();
  const { formatMessage } = useIntl();
  const {
    error: apiError,
    loading,
    data,
  } = useApi(useCallback(() => getTOSStatus(sessionJWT), [sessionJWT]));
  const toast = useToast();
  const [completeState, setCompleteState] = useState(null);

  const handleComplete = useCallback(
    (d) => {
      onComplete(d);
      setCompleteState(d);
    },
    [onComplete]
  );

  useEffect(() => {
    onReady({ state: FLOW_STATE.READY });
  }, [onReady]);

  useEffect(() => {
    if (R.pathEq(["payload", "signed_tos"], true)(data)) {
      handleComplete({ state: "complete" });
    }
  }, [handleComplete, data]);

  if (completeState) {
    return <div data-testid={`tos-form-complete`}></div>;
  }
  if (apiError) {
    // If user waits long enough to open the link and we still have a token in storage
    // that has expired then we are likely to get a 401 here
    const tokenInvalid = apiError instanceof TiliaAPIError && apiError.status === 401;
    let content = <EmptyState error={apiError} />;
    if (tokenInvalid) {
      // If we receive a 401 error at this stage, it is highly likely that we are using
      // an expired token for the user
      content = (
        <EmptyState
          title="Invalid link"
          description="This is usually the result of expired data."
        />
      );
    }
    const message = tokenInvalid
      ? formatMessage({
          defaultMessage: "Invalid link.  This is usually the result of expired data.",
          description: "Invalid link. error message",
        })
      : apiError.message ||
        formatMessage({
          defaultMessage: "Error retrieving invoice",
          description: "error message",
        });
    onError({
      message,
      error: apiError,
    });
    return content;
  }
  if (R.pathEq(["payload", "signed_tos"], true)(data)) {
    return <div data-testid={`tos-form-result`}></div>;
  }
  if (loading) {
    return null;
  }

  const doSubmit = async () => {
    try {
      await signTiliaTOS(sessionJWT);
      handleComplete({ state: "complete" });
    } catch (error) {
      console.error(error);

      let errorDescription = formatMessage({
        defaultMessage: "Something has gone wrong. Please try again later.",
        description: "error message",
      });
      if (error instanceof Error) {
        errorDescription = error.message;
      }

      toast({
        title: formatMessage({
          defaultMessage: "Oops!",
          description: "alert title",
        }),
        description: errorDescription,
        status: "error",
        duration: null,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH={MIN_HEIGHT}>
      <VStack spacing="5" align="stretch">
        <Header
          onCloseButtonClick={!hideExitButtons && (() => handleComplete({ state: "cancel" }))}
          title={formatMessage({
            defaultMessage: "Tilia Terms Of Service",
            description: "Heading",
          })}
          showLanguage
        />
        <Formik
          initialValues={{
            tosAndPpCheckbox: false,
          }}
          initialErrors={{
            tosAndPpCheckbox: true,
          }}
          validationSchema={Yup.object().shape({
            tosAndPpCheckbox: Yup.bool().oneOf([true]),
          })}
          onSubmit={doSubmit}
          validateOnMount
        >
          {(props) => <FormContents {...props} handleComplete={handleComplete} />}
        </Formik>
      </VStack>
    </Box>
  );
};

const allowedBypassTOS = R.compose(isBooleanTrue, R.prop(ALLOWBYPASSTOS));
const requestBypassTOS = R.compose(isTrue, R.prop(REQUESTBYPASSTOS));

export const TOSForm = (props) => {
  const analytics = useAnalytics();
  const { config = {}, flow, onComplete } = props;
  const { debug, sessionCtx } = useSessionCtx();
  const { flowConfig = {} } = sessionCtx;
  const flowConfigSettings = config[flow] || {};
  debug("flowConfigSettings: ", flowConfigSettings);
  const viewEventFired = useRef(false);

  const allowedBypass = allowedBypassTOS(flowConfigSettings);
  const requestedBypass = requestBypassTOS(flowConfig);
  const finalBypass = allowedBypass && requestedBypass;
  debug("bypass tos? ", allowedBypass, requestedBypass, finalBypass);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("TOS", "TOS_Viewed", { isBypassed: finalBypass });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, finalBypass]);

  useEffect(() => {
    if (finalBypass) {
      onComplete({ state: FLOW_STATE.BYPASS });
    }
  }, [finalBypass, debug, onComplete]);

  return finalBypass ? <div data-testid="tos-bypass" /> : <InnerTOSForm {...props} />;
};
